import React from 'react';

import { Icon } from '@/nova/components';
import type { StoryboardPromotion } from '@/store/slices/creativeJobs';

import {
  ChipLabel,
  Chip,
  Chips,
  ChooseBtn,
  ChooseLabel,
  Label,
  OrderBadge,
  RemoveBtn,
  Root,
  Thumb,
  ThumbImg,
} from './PromotionsField.styles';

export interface PromotionsFieldProps {
  /** Field label (i18n key), matching the sibling Select/TextArea labels. */
  label: string;
  /** Label for the "choose promotions" trigger (i18n key). */
  buttonLabel: string;
  /** The selected promotions, in play order. promotions[0] is the key visual (F0). */
  promotions: StoryboardPromotion[];
  /** Fallback chip label when a promotion has no headline (i18n key). */
  emptyChipLabel: string;
  /** Open the promotion picker modal. */
  onOpen: () => void;
  /** Remove one promotion from the selection (by banner id). */
  onRemove: (bannerId: string) => void;
  /** Accessible label for a chip's remove control, given the promotion's name. */
  removeAriaLabel: (name: string) => string;
  disabled?: boolean;
  /** Leave the label out (the caller titles the field itself, e.g. an accordion section). */
  hideLabel?: boolean;
}

/**
 * The composer's promotion picker control: the selected promotions rendered as ordered, removable chips
 * (each with its key-art thumbnail and 1-based play-order badge), plus a "Choose promotions" button that
 * opens the {@link PoolPickerSheet} modal. promotions[0] is still the key visual (F0) functionally, but no
 * chip is visually highlighted. The selection itself lives in the composer (this is presentational); the
 * modal is what edits it.
 */
export const PromotionsField = ({
  label,
  buttonLabel,
  promotions,
  emptyChipLabel,
  onOpen,
  onRemove,
  removeAriaLabel,
  disabled = false,
  hideLabel = false,
}: PromotionsFieldProps): React.ReactElement => (
  <Root>
    {hideLabel ? null : <Label text={label} tag="span" />}
    {promotions.length > 0 ? (
      <Chips>
        {promotions.map((p, i) => {
          const name = p.headline?.trim() || emptyChipLabel;
          return (
            <Chip key={p.banner_id || p.image_url || i}>
              <OrderBadge aria-hidden="true">{i + 1}</OrderBadge>
              <Thumb>{p.image_url ? <ThumbImg src={p.image_url} alt="" loading="lazy" /> : null}</Thumb>
              <ChipLabel text={name} />
              <RemoveBtn
                type="button"
                aria-label={removeAriaLabel(name)}
                disabled={disabled}
                onClick={() => {
                  if (disabled) return;
                  onRemove(p.banner_id);
                }}
              >
                <Icon name="close" size={14} />
              </RemoveBtn>
            </Chip>
          );
        })}
      </Chips>
    ) : null}
    <ChooseBtn type="button" disabled={disabled} onClick={() => !disabled && onOpen()}>
      <Icon name="plus" size={16} />
      <ChooseLabel text={buttonLabel} />
    </ChooseBtn>
  </Root>
);
